import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useResolvedOwnerId } from '@/hooks/analytics/useResolvedOwnerId';
import { briefWorkspaceQueryKey } from '@/hooks/useBriefWorkspace';
import {
  type BriefConcept,
  type BriefConceptWrite,
  briefConceptEditorService,
} from '@/services/data/BriefConceptEditorService';

export const briefConceptQueryKey = (
  ownerId: string | null | undefined,
  briefId: string | null | undefined,
  projectTaskId: string | null | undefined,
) => ['brief-concept', ownerId ?? null, briefId ?? null, projectTaskId ?? null] as const;

export function useBriefConcept(
  briefId: string | null | undefined,
  projectTaskId: string | null | undefined,
) {
  const { data: ownerId } = useResolvedOwnerId(Boolean(briefId && projectTaskId));
  const queryClient = useQueryClient();
  const queryKey = briefConceptQueryKey(ownerId, briefId, projectTaskId);
  const invalidateBrief = () => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: ['brief-workspace'] });
    void queryClient.invalidateQueries({ queryKey: ['campaigns', 'briefs'] });
  };

  const query = useQuery({
    queryKey,
    enabled: Boolean(ownerId && briefId && projectTaskId),
    queryFn: () => briefConceptEditorService.get(briefId!, projectTaskId!),
  });

  const update = useMutation({
    mutationFn: (
      input: BriefConceptWrite & { expectedVersion: number; referenceUrls?: string[] },
    ) => {
      if (!briefId || !projectTaskId) throw new Error('Brief Concept not found.');
      return briefConceptEditorService.update({ briefId, projectTaskId, ...input });
    },
    onSuccess: (concept) => {
      queryClient.setQueryData(queryKey, concept);
      invalidateBrief();
    },
  });

  const archive = useMutation({
    mutationFn: (expectedVersion: number) => {
      if (!briefId || !projectTaskId) throw new Error('Brief Concept not found.');
      return briefConceptEditorService.archive({ briefId, projectTaskId, expectedVersion });
    },
    onSuccess: (concept) => {
      queryClient.setQueryData(queryKey, concept);
      invalidateBrief();
    },
  });

  const restore = useMutation({
    mutationFn: (expectedVersion: number) => {
      if (!briefId || !projectTaskId) throw new Error('Brief Concept not found.');
      return briefConceptEditorService.restore({ briefId, projectTaskId, expectedVersion });
    },
    onSuccess: (concept) => {
      queryClient.setQueryData(queryKey, concept);
      invalidateBrief();
    },
  });

  return { ...query, update, archive, restore };
}

/**
 * Starts a manual Concept through the same atomic RPC as every other Concept
 * write. The detail page deliberately owns the picker and navigation so this
 * hook only handles persistence and cache coherence.
 */
export function useCreateBriefConcept(briefId: string | null | undefined) {
  const { data: ownerId } = useResolvedOwnerId(Boolean(briefId));
  const queryClient = useQueryClient();

  return useMutation({
    // The caller retains one commandId across reconciliation attempts. Keep
    // transport retries explicit so an ambiguous response is surfaced instead
    // of being retried outside that command lifecycle.
    retry: false,
    mutationFn: (
      input: Omit<BriefConceptWrite, 'briefId'> & {
        commandId: string;
        referenceUrls?: string[];
      },
    ) => {
      if (!briefId) throw new Error('Brief not found.');
      return briefConceptEditorService.create({ briefId, ...input });
    },
    onSuccess: (concept: BriefConcept) => {
      queryClient.setQueryData(briefConceptQueryKey(ownerId, briefId, concept.id), concept);
      void queryClient.invalidateQueries({ queryKey: briefWorkspaceQueryKey(ownerId, briefId) });
      void queryClient.invalidateQueries({ queryKey: ['campaigns', 'briefs'] });
    },
  });
}
